// React frontend configuration
// This file handles environment-specific configuration for the frontend

// CRITICAL: For production builds, ALWAYS use HTTPS API URL
// This is the ONLY source of truth for the API URL in production
const PRODUCTION_API_URL = 'https://api.padpal.fr/api/v1';

// PadelSkill SSO URL - where users are redirected when clicking "Open PadelSkill"
const PADELSKILL_URL = process.env.REACT_APP_PADELSKILL_URL || 'https://padelskill.saffist3r.xyz';

// Default configuration (fallback) - ALWAYS HTTPS for production
const DEFAULT_CONFIG = {
  API_URL: PRODUCTION_API_URL,
  PADELSKILL_URL,
  DEBUG: false,
  LOG_LEVEL: 'info',
};

// Environment configuration
const getEnvironment = () => {
  // Check for React environment variables
  if (process.env.REACT_APP_ENVIRONMENT) {
    return process.env.REACT_APP_ENVIRONMENT.toUpperCase();
  }
  
  // Check for NODE_ENV
  if (process.env.NODE_ENV) {
    return process.env.NODE_ENV.toUpperCase();
  }
  
  // Fallback to development
  return 'DEVELOPMENT';
};

// Development configuration - ALWAYS HTTPS (even for localhost, use https://localhost:8000)
const DEV_CONFIG = {
  API_URL: process.env.REACT_APP_DEV_API_URL || 'https://localhost:8000/api/v1',
  PADELSKILL_URL,
  DEBUG: true,
  LOG_LEVEL: 'debug',
};

// Production configuration - ALWAYS HTTPS (no HTTP fallback)
const PROD_CONFIG = {
  API_URL: PRODUCTION_API_URL,
  PADELSKILL_URL,
  DEBUG: false,
  LOG_LEVEL: 'info',
};

// Helper function to force HTTPS for ALL URLs (no exceptions)
const forceHttps = (url: string): string => {
  if (!url) return url;
  // ALWAYS force HTTPS - no exceptions, even for localhost
  return url.replace(/^http:/i, 'https:');
};

// Helper to check if we're in production (not localhost)
const isProduction = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname !== 'localhost' && 
         hostname !== '127.0.0.1' && 
         !hostname.startsWith('192.168.') &&
         !hostname.startsWith('10.') &&
         hostname !== '';
};

// Load configuration based on environment
const loadConfig = () => {
  let apiUrl: string;
  
  // CRITICAL: If we're in a browser on a production domain, ALWAYS use HTTPS
  // This check happens FIRST before any environment variable checks
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isProductionDomain = hostname !== 'localhost' && 
                              hostname !== '127.0.0.1' && 
                              !hostname.startsWith('192.168.') &&
                              !hostname.startsWith('10.') &&
                              hostname !== '';
    
    // If we're on a production domain (like padpal.fr), ALWAYS use HTTPS API URL (hardcoded)
    if (isProductionDomain) {
      apiUrl = PRODUCTION_API_URL; // This is already HTTPS
      return {
        API_URL: apiUrl,
        PADELSKILL_URL,
        DEBUG: false,
        LOG_LEVEL: 'info',
      };
    }

    // Local dev: use same host as frontend with port 8000 (Docker API)
    apiUrl = `http://${hostname}:8000/api/v1`;
    return {
      API_URL: apiUrl,
      PADELSKILL_URL,
      DEBUG: true,
      LOG_LEVEL: 'debug',
    };
  }
  
  // For build-time configuration (when window is undefined)
  // Check for explicit API URL first (highest priority)
  if (process.env.REACT_APP_API_URL) {
    apiUrl = process.env.REACT_APP_API_URL;
  } else {
    const environment = getEnvironment();
    
    // Use environment-specific config
    if (environment === 'DEVELOPMENT' || environment === 'DEV') {
      apiUrl = DEV_CONFIG.API_URL;
    } else if (environment === 'PRODUCTION' || environment === 'PROD') {
      apiUrl = PROD_CONFIG.API_URL;
    } else if (process.env.NODE_ENV === 'production') {
      apiUrl = PROD_CONFIG.API_URL;
    } else {
      // Default to production config for safety
      apiUrl = DEFAULT_CONFIG.API_URL;
    }
  }
  
  // CRITICAL: ALWAYS force HTTPS for ALL URLs (no exceptions)
  apiUrl = forceHttps(apiUrl);
  
  return {
    API_URL: apiUrl,
    PADELSKILL_URL,
    DEBUG: process.env.REACT_APP_DEBUG === 'true',
    LOG_LEVEL: process.env.REACT_APP_LOG_LEVEL || 'info',
  };
};

// Load configuration
let config = loadConfig();

// Export a getter function to ensure we always get the latest config with HTTPS enforcement
export const getConfig = () => {
  // Only force HTTPS for production domains; allow HTTP for local dev
  const isDevHost = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.') ||
    window.location.hostname.startsWith('10.')
  );
  let apiUrl = isDevHost ? config.API_URL : forceHttps(config.API_URL);

  return {
    API_URL: apiUrl,
    PADELSKILL_URL: config.PADELSKILL_URL,
    DEBUG: config.DEBUG,
    LOG_LEVEL: config.LOG_LEVEL,
  };
};

// Debug logging - always log in production to help diagnose HTTPS issues
if (typeof window !== 'undefined') {
  const finalConfig = getConfig();
  if (finalConfig.API_URL.startsWith('http://') && !finalConfig.API_URL.includes('localhost')) {
    console.error('⚠️ SECURITY WARNING: API URL is HTTP but should be HTTPS!', finalConfig.API_URL);
  }
  if (window.location.protocol === 'https:' && finalConfig.API_URL.startsWith('http://') && !finalConfig.API_URL.includes('localhost')) {
    console.error('❌ CRITICAL: Page is HTTPS but API URL is HTTP! This will cause mixed content errors!', finalConfig.API_URL);
  }
  if (config.DEBUG || process.env.NODE_ENV === 'development') {
    console.log('🔧 Frontend Environment Configuration:');
    console.log(`   Environment: ${getEnvironment()}`);
    console.log(`   Page Protocol: ${window.location.protocol}`);
    console.log(`   API URL: ${finalConfig.API_URL}`);
    console.log(`   Debug: ${finalConfig.DEBUG}`);
    console.log(`   Log Level: ${finalConfig.LOG_LEVEL}`);
  }
}

/** Build full URL for upload paths (e.g. /uploads/avatars/foo.jpg). */
export const getFullUploadUrl = (path: string): string => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const { API_URL } = getConfig();
  const origin = new URL(API_URL).origin;
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
};

export { config }; 